import { Skeleton, TableProps, getTableMetaHelper, SkeletonTableCell } from '@round/ui-kit';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import styles from '../PostsTable.module.css';
import CostCell from 'Modules/Plans/components/CostCell/CostCell';
import { CampaignTableRow } from '../../ProjectCampaignsTable/ProjectCampaignsTable';
import TotalFooterCell from '../../../../components/TotalFooterCell';
import TotalCostFooterCell from 'Modules/Plans/components/TotalCostFooterCell/TotalCostFooterCell';
import ActionsCell from '../components/ActionsCell/ActionsCell';
import NotesCell from 'Modules/Plans/components/NotesCell/NotesCell';
import { getYoutubeEngagementRatePercentageString, getYoutubeTotalEngagement } from 'Modules/Plans/Posts/helpers';
import PostsTable from 'Modules/Plans/Posts/components/PostsTable/PostsTable';
import { BasePostTableMeta } from 'Modules/Plans/Posts/types';
import { useAggregatedPostStats } from '@round/creatorbase-helpers/api-hooks';
import UpdateInfluencerPopout from '../components/UpdateInfluencerPopout/UpdateInfluencerPopout';
import { CopyButton } from '@round/creatorbase-helpers/components';
import cn from 'classnames';
import PostsTableEngagementRateFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementRateFooterCell';
import PostsTableEngagementsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableEngagementsFooterCell';
import PostStatsValueCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsStatsValueCell';
import PostStatsViewsFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostStatsViewsFooterCell';
import PostsTableUploadedDateCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableUploadedDateCell';
import PostsTableStatusCell, {
    StatusData,
} from 'Modules/Plans/Posts/components/PostsTable/cells/StatusCell/PostsTableStatusCell';
import UserBadge, { UserBadgeProps } from 'Modules/Plans/User/UserBadge/UserBadge';
import { YoutubePostsTableRow } from '../../../hooks/posts/useProjectDetailsYoutubePosts';
import { creatorbase } from '@round/api';
import { isReceivedCampaign } from 'Modules/Plans/Campaigns/helpers';
import ManualPaymentStatusCell from '../components/ManualPaymentStatusCell/ManualPaymentStatusCell';
import EstimatedViewsCell from '../components/EstimatedViewsCell/EstimatedViewsCell';
import ContactDetailsCell from '../components/ContactDetailsCell/ContactDetailsCell';
import UpdateCostCell from '../components/UpdateCostCell/UpdateCostCell';
import PaymentStatusCell from '../components/PaymentStatusCell/PaymentStatusCell';
import PostsTableStatFooterCell from 'Modules/Plans/Posts/components/PostsTable/cells/PostsTableStatFooterCell';
import { cpm } from 'Modules/Advertising/Metrics/Metrics';
import { asMoneyWithoutZeros, buildYoutubeChannelUrl } from 'helpers';
import { TooltipHeader } from '@round/whitelabel-ui';
import { tooltipCopy } from 'Modules/Plans/helpers';

type Props = Pick<TableProps<YoutubePostsTableRow>, 'data' | 'noDataLabel'> & {
    campaign: CampaignTableRow;
    isLoading?: boolean;
    isAccountDataLoading?: boolean;
    hasError: boolean;
    onCreatePost?: (campaign: CampaignTableRow) => void;
    onDeletePost: (post: YoutubePostsTableRow) => void;
    onOpenPostStatsModal?: (post: YoutubePostsTableRow) => void;
    updatePost: typeof creatorbase.patchPost;
    isPaymentEnabled?: boolean;
    onPostStatusUpdated?: (status: creatorbase.PostManualStatus) => void;
};

type TableCellContext<TKey extends keyof YoutubePostsTableRow> = CellContext<
    YoutubePostsTableRow,
    YoutubePostsTableRow[TKey]
>;

type Meta = BasePostTableMeta<YoutubePostsTableRow> & {
    campaign: CampaignTableRow;
    onDeletePost: (post: YoutubePostsTableRow) => void;
};
const getTableMeta = getTableMetaHelper<Meta>();

const YoutubePostsTable = ({
    data,
    campaign,
    isLoading,
    isAccountDataLoading,
    hasError,
    onCreatePost,
    onDeletePost,
    onOpenPostStatsModal,
    updatePost,
    isPaymentEnabled,
    onPostStatusUpdated,
}: Props) => {
    const { isFetching: areCampaignStatsLoading } = useAggregatedPostStats({ campaign_id: campaign.id });

    const columns = useMemo<ColumnDef<YoutubePostsTableRow, any>[]>(
        () => [
            {
                header: 'ID',
                accessorKey: 'id',
                cell: ({ getValue, table }: TableCellContext<'id'>) => {
                    const { isLoading } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return getValue().slice(-8);
                },
            },
            {
                header: 'Account',
                accessorKey: 'channel',
                cell: ({ getValue, row: { original }, table }) => {
                    const { isLoading, getIsAccountDataLoading, updatePost, managedBy, isReceived } =
                        getTableMeta(table);
                    const isManagedExternally = managedBy !== 'creatorbase' || !!isReceived;
                    const channelName = getValue()?.title;

                    const userBadgeProps: UserBadgeProps = {
                        isLoading: isLoading || getIsAccountDataLoading(original),
                        image: getValue()?.thumbnail,
                        title: channelName || '-',
                        profileUrl: getValue()?.youtube_id ? buildYoutubeChannelUrl(getValue().youtube_id) : null,
                    };

                    return (
                        <div className={styles.accountContainer}>
                            <UserBadge {...userBadgeProps} />

                            <div className={cn(styles.actionsContainer, 'twp')}>
                                {channelName && (
                                    <CopyButton
                                        value={channelName}
                                        tooltipLabel="Copy channel name"
                                        variant="ghost"
                                        size="icon-sm"
                                        className={cn(styles.iconButton, styles.hoverActionButton)}
                                    />
                                )}
                                {!original.post_url && !isManagedExternally && (
                                    <UpdateInfluencerPopout
                                        key={channelName}
                                        className={cn(styles.iconButton, styles.hoverActionButton)}
                                        accountHandle={channelName ?? ''}
                                        updateAccountHandle={
                                            updatePost
                                                ? (accountHandle) =>
                                                      updatePost?.(original.id, {
                                                          influencer_account_identifier: accountHandle,
                                                      })
                                                : undefined
                                        }
                                    />
                                )}
                            </div>
                        </div>
                    );
                },
            },
            {
                header: 'Contact details',
                id: 'contactDetails',
                accessorKey: 'contact_details',
                cell: ContactDetailsCell,
            },
            {
                header: 'Status',
                id: 'status',
                accessorFn: (original): StatusData => ({
                    postUrl: original.post_url,
                    platform: original.platform,
                }),
                cell: PostsTableStatusCell,
            },
            {
                header: 'Cost',
                accessorKey: 'cost',
                cell: ({ getValue, row: { original }, table }: TableCellContext<'cost'>) => {
                    const { isLoading, updatePost, campaign } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <CostCell
                            cost={getValue()}
                            currency={campaign.currency ?? null}
                            updateCost={
                                updatePost ? (cost: number | null) => updatePost(original.id, { cost }) : updatePost
                            }
                            isReadonly={!campaign.is_post_cost_editable}
                        />
                    );
                },
                footer: ({ table }) => {
                    const { isLoading, campaign } = getTableMeta(table);

                    return isLoading ? (
                        <Skeleton />
                    ) : (
                        <TotalCostFooterCell
                            costs={table.getRowModel().rows.map((r) => r.original.cost)}
                            currency={campaign.currency}
                        />
                    );
                },
            },
            {
                header: 'Cost',
                accessorKey: 'cost',
                id: 'costWithFees',
                cell: UpdateCostCell,
                footer: ({ table }) => {
                    const { isLoading, campaign } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return (
                        <TotalCostFooterCell
                            costs={table.getRowModel().rows.map((r) => r.original.cost)}
                            currency={campaign.currency}
                        />
                    );
                },
            },
            {
                header: 'Payment Status',
                id: 'manualPaymentStatus',
                accessorKey: 'payment_status',
                cell: ManualPaymentStatusCell,
            },
            {
                header: 'Payment Status',
                id: 'paymentStatus',
                accessorKey: 'payment_request_status',
                cell: PaymentStatusCell,
            },
            {
                header: 'Uploaded date',
                id: 'uploadedDate',
                cell: PostsTableUploadedDateCell,
            },
            {
                header: 'Est. views',
                meta: {
                    className: styles.estimatedViewsCell,
                },
                id: 'estimatedViews',
                accessorKey: 'estimated_views',
                cell: EstimatedViewsCell,
                footer: PostsTableStatFooterCell,
            },

            {
                header: 'Est. CPM',
                id: 'estimatedCPM',
                accessorFn: (row) => {
                    const views = row.estimated_views;
                    const estimatedCpm = cpm(row.cost, views);
                    return isFinite(estimatedCpm) ? estimatedCpm : '-';
                },
                cell: ({ table, getValue }) => {
                    const { isLoading, campaign } = getTableMeta(table);
                    if (isLoading) {
                        return <Skeleton />;
                    }

                    const cpm = getValue();
                    return typeof cpm === 'number' ? asMoneyWithoutZeros(cpm, campaign.currency) : '-';
                },
                footer: ({ table }) => {
                    const { isLoading, campaign } = getTableMeta(table);
                    if (isLoading) {
                        return <Skeleton />;
                    }

                    const rows = table.getRowModel().rows;
                    const estimatedViews = rows.reduce((acc, { original: row }) => {
                        const estimatedViews = row.estimated_views ?? 0;
                        return estimatedViews + acc;
                    }, 0);

                    const cost = rows.reduce((acc, { original: row }) => {
                        return acc + (row.cost ?? 0);
                    }, 0);

                    const estimatedCpm = cpm(cost, estimatedViews);
                    return isFinite(estimatedCpm) ? asMoneyWithoutZeros(estimatedCpm, campaign.currency) : '-';
                },
            },
            {
                header: 'Views',
                accessorKey: 'view_count',
                cell: PostStatsValueCell,
                footer: PostStatsViewsFooterCell,
            },
            {
                header: 'Total engagements',
                id: 'totalEngagements',
                accessorFn: (row) => getYoutubeTotalEngagement(row),
                cell: PostStatsValueCell,
                footer: PostsTableEngagementsFooterCell,
            },
            {
                header: () => <TooltipHeader header="ER" tooltipBody={tooltipCopy.engagementRate} />,
                id: 'engagementRate',
                accessorFn: (row) => getYoutubeEngagementRatePercentageString(row),
                cell: SkeletonTableCell,
                footer: PostsTableEngagementRateFooterCell,
            },
            {
                header: 'Likes',
                id: 'likes',
                accessorFn: (original) => original.youtube_details?.like_count,
                cell: PostStatsValueCell,
                footer: ({ table }) => {
                    const { isLoading } = getTableMeta(table);

                    return isLoading ? (
                        <Skeleton />
                    ) : (
                        <TotalFooterCell
                            values={table.getRowModel().rows.map((r) => r.original.youtube_details?.like_count)}
                        />
                    );
                },
            },
            {
                header: 'Comments',
                id: 'comments',
                accessorFn: (original) => original.youtube_details?.comment_count,
                cell: PostStatsValueCell,
                footer: ({ table }) => {
                    const { isLoading } = getTableMeta(table);

                    return isLoading ? (
                        <Skeleton />
                    ) : (
                        <TotalFooterCell
                            values={table.getRowModel().rows.map((r) => r.original.youtube_details?.comment_count)}
                        />
                    );
                },
            },
            {
                header: 'Notes',
                accessorKey: 'notes',
                cell: ({ getValue, row: { original }, table }: TableCellContext<'notes'>) => {
                    const { isLoading, updatePost } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }
                    return (
                        <NotesCell
                            notes={getValue()}
                            updateNotes={updatePost ? (notes: string) => updatePost(original.id, { notes }) : undefined}
                        />
                    );
                },
            },
            {
                header: '',
                id: 'actions',
                cell: ({ row: { original }, table }) => {
                    const { isLoading, onDeletePost } = getTableMeta(table);

                    if (isLoading) {
                        return <Skeleton />;
                    }

                    return <ActionsCell onDelete={() => onDeletePost(original)} />;
                },
            },
        ],
        []
    );

    // meta object is passed to every cell component. This allows us to reduce rerenders and prevents unnecessary cell unmounts.
    const isReceived = isReceivedCampaign(campaign);

    const meta: Meta = {
        isLoading: !!isLoading,
        getIsAccountDataLoading: (row) => (row.youtube_details?.account_id ? !!isAccountDataLoading : false),
        areCampaignStatsLoading,
        onDeletePost,
        onOpenPostStatsModal,
        onCreatePost: onCreatePost ? () => onCreatePost(campaign) : undefined,
        updatePost,
        campaign,
        campaignStats: campaign.stats,
        managedBy: campaign.managed_by,
        isReceived,
        onPostStatusUpdated,
    };

    const shouldShowIntegratedPaymentColumns = Boolean(
        !isReceived && campaign.is_post_cost_editable && campaign.managed_by === 'creatorbase' && isPaymentEnabled
    );

    const shouldShowManualPaymentColumns = Boolean(
        !isReceived && campaign.is_post_cost_editable && campaign.managed_by === 'creatorbase' && !isPaymentEnabled
    );

    return (
        <PostsTable
            className={styles.table}
            columns={columns}
            data={data}
            meta={meta}
            isLoading={isLoading}
            hasError={hasError}
            columnVisibility={{
                contactDetails: campaign.managed_by === 'creatorbase' && !isReceived,
                costWithFees: shouldShowIntegratedPaymentColumns,
                paymentStatus: shouldShowIntegratedPaymentColumns,
                cost: shouldShowManualPaymentColumns,
                manualPaymentStatus: shouldShowManualPaymentColumns,
                estimatedCPM: !isReceived && campaign.is_post_cost_editable,
                actions: campaign.managed_by === 'creatorbase' && !isReceived,
                notes: !isReceived,
            }}
        />
    );
};

export default YoutubePostsTable;
